// src/router/routeWrappers.tsx
import React from 'react';
import { useParams, useNavigate, Navigate, NavigateFunction } from 'react-router-dom';

/**
 * Contract every add/edit screen must satisfy.
 *
 *   id === undefined  →  "New" mode  (blank form)
 *   id === string     →  "Edit" mode (load existing record)
 */
export interface CrudProps {
  id?: string;  // ✅ Changed from number to string
  onNavigate: NavigateFunction;
}

/**
 * Factory that produces { New, Edit } route components for any CRUD screen.
 *
 * @param Component   - The screen component (must implement CrudProps)
 * @param fallbackPath - Where to redirect if :id in the URL is missing
 *
 * Example
 * -------
 *   const Letterhead = createCrudRoutes(SchoolLetterHeader, '/letterheads');
 *   const Student    = createCrudRoutes(StudentForm,        '/students');
 *
 *   <Route path="letterheads/new"       element={<Letterhead.New />} />
 *   <Route path="letterheads/edit/:id"  element={<Letterhead.Edit />} />
 */
export function createCrudRoutes(
  Component: React.ComponentType<CrudProps>,
  fallbackPath: string,
) {
  // ── /some-path/new ──────────────────────────────────────────────────────────
  const New: React.FC = () => {
    const navigate = useNavigate();
    return <Component key="new" onNavigate={navigate} />;
  };
  New.displayName = `${Component.displayName ?? Component.name}New`;

  // ── /some-path/edit/:id ─────────────────────────────────────────────────────
  const Edit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    // ✅ Keep id as string - do NOT convert to number
    if (!id) return <Navigate to={fallbackPath} replace />;

    // key={`edit-${id}`} forces a remount whenever the id changes,
    // so navigating edit/abc → edit/xyz never leaks stale state.
    return <Component key={`edit-${id}`} id={id} onNavigate={navigate} />;
  };
  Edit.displayName = `${Component.displayName ?? Component.name}Edit`;

  return { New, Edit };
}